import React, {useEffect, useState} from 'react';
import useCollectionParams from '@app/hooks/useCollectionParams';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
} from '@mui/material';
import {DialogTitle} from '@apps-common/ui-components';
import {MAX_TITLE_LENGTH} from '@app/constants';
import {bool, func, object} from 'prop-types';
import {notify} from '@actions/notifications';
import {trackEvent} from '@app/analytics';
import {useDispatch} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {useUpdateCollection} from '@app/api/collections';

const EVENT_NAME = 'Edit Collection Modal';

const EditTitleModal = ({open, onClose, collection}) => {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const {collectionId, setCollection} = useCollectionParams();

  const [title, setTitle] = useState('');
  const [helperText, setHelperText] = useState(false);

  const {mutate: updateCollection, isPending: isUpdatingCollection} =
    useUpdateCollection({
      onSuccess: data => {
        trackEvent(EVENT_NAME, {action: 'Edited Title'});
        if (collectionId) {
          setCollection(data.id);
        }
        onClose();
      },
      onError: () => {
        dispatch(
          notify({
            message: t('editCollectionTitleModal.error'),
            severity: 'error',
          })
        );
      },
    });

  useEffect(() => {
    if (collection.title) {
      setTitle(collection.title);
    }
  }, [collection]);

  useEffect(() => {
    if (title.length >= MAX_TITLE_LENGTH) {
      setHelperText(true);
    } else {
      setHelperText(false);
    }
  }, [title.length]);

  const onSave = () => {
    updateCollection({collectionId: collection.id, data: {title}});
  };

  const handleOnClose = () => {
    trackEvent(EVENT_NAME, {
      action: 'closed modal',
    });

    onClose();
  };

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={handleOnClose}>
      <DialogTitle
        onClose={handleOnClose}
        title={t('editCollectionTitleModal.title')}
      />
      <DialogContent>
        <TextField
          fullWidth
          value={title}
          onChange={evt => setTitle(evt.target.value)}
          inputProps={{
            maxLength: MAX_TITLE_LENGTH,
            'data-testid': 'edit-title-input',
          }}
          helperText={
            helperText ? t('editCollectionTitleModal.helperText') : ' '
          }
          error={!!helperText}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleOnClose} variant="outlined">
          {t('common:cancelBtn')}
        </Button>
        <Button
          color="primary"
          variant="contained"
          disabled={isUpdatingCollection || title === collection.title}
          onClick={onSave}
        >
          {isUpdatingCollection
            ? t('common:loadingSaveBtn')
            : t('common:saveBtn')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

EditTitleModal.propTypes = {
  collection: object,
  onClose: func,
  open: bool,
};

export default EditTitleModal;
